
import React, { useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { ShoppingBag, ArrowRight, AlertCircle } from 'lucide-react';
import { useCartStore } from '@/store/useCartStore';
import CartItem from './CartItem';
import { formatCurrency } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import TranslatedText from '@/TranslatedText';

const CartModal = () => {
  const {
    items,
    isOpen,
    closeCart,
    totalItems,
    totalPrice,
    clearCart
  } = useCartStore();
  const navigate = useNavigate();

  // Close cart modal on route change
  useEffect(() => {
    const handleRouteChange = () => {
      closeCart();
    };
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, [closeCart]);
  
  const handleCheckout = () => {
    closeCart();
    navigate('/checkout');
    window.scrollTo(0, 0);
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="flex flex-col w-[80%] sm:max-w-md p-4 z-[200]">
        <SheetHeader className="px-1">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 font-bold text-xl text-ahavex-blue">
              <ShoppingBag size={20} />
              <TranslatedText text="Your Cart" />
              {totalItems() > 0 && (
                <span className="ml-2 text-sm bg-ahavex-blue text-white px-2 py-0.5 rounded-full">
                  {totalItems()}
                </span>
              )}
            </SheetTitle>
            {/* Removed the duplicate close button that was here */}
          </div>
        </SheetHeader>
        
        <Separator className="my-4" />
        
        <div className="flex-1 overflow-y-auto pr-2">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <div className="bg-gray-50 p-6 rounded-full mb-4">
                <ShoppingBag size={40} className="text-gray-400" />
              </div>
              <h3 className="font-medium text-lg mb-2">
                <TranslatedText text="Your cart is empty" />
              </h3>
              <p className="text-gray-500 mb-6 max-w-xs">
                <TranslatedText text="Looks like you haven't added any products to your cart yet." />
              </p>
              <Button 
                onClick={closeCart} 
                className="bg-ahavex-blue hover:bg-ahavex-darkblue text-white"
              >
                <TranslatedText text="Continue Shopping" />
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => <CartItem key={item.id} item={item} />)}
            </div>
          )}
        </div>
        
        {items.length > 0 && (
          <>
            <Separator className="my-4" />
            
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  <TranslatedText text="Subtotal" />
                </span>
                <span>{formatCurrency(totalPrice(), items[0]?.currency || 'RWF')}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">
                  <TranslatedText text="Shipping" />
                </span>
                <span className="text-green-600">
                  <TranslatedText text="Free" />
                </span>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between font-medium">
                <span>
                  <TranslatedText text="Total" />
                </span>
                <span className="text-lg">{formatCurrency(totalPrice(), items[0]?.currency || 'RWF')}</span>
              </div>
              
              <div className="flex gap-2 mt-2">
                <Button 
                  variant="outline" 
                  onClick={clearCart} 
                  className="flex-1 text-black font-medium"
                >
                  <TranslatedText text="Clear Cart" />
                </Button>
                <Button 
                  onClick={handleCheckout}
                  className="flex-[2] bg-ahavex-blue hover:bg-ahavex-darkblue text-white"
                >
                  <TranslatedText text="Checkout" /> <ArrowRight size={16} />
                </Button>
              </div>
              
              <div className="text-xs text-gray-500 flex items-start gap-1 mt-2">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <TranslatedText 
                  text="Shipping and taxes will be calculated at checkout. Free shipping on orders over RWF 100k."
                />
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartModal;
